import PropTypes from 'prop-types';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { PaymentInfoModalButton, InfoBanner, Button } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import { Mediator } from '@spotahome/soyuz-mediator';

import ListRow from '@spotahome/marketplace-common/src/components/ListRow';
import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';
import {
  OPEN_BOOKNOW_DATEPICKER_DESKTOP,
  OPEN_BOOKNOW_DATEPICKER_MOBILE
} from '@spotahome/marketplace-common/src/channels';

import { ListingSectionObservable } from '../ListingSection';

import './ListingPaymentsModule.scss';

const ListingPaymentsModule = ({ hasDates, deviceType }) => {
  const isListingSimplificationBottomVariant =
    useSoyuzExperiment('ListingSimplificationBottom').get('behaviour') ===
    'variant';
  const { priceRange, formatPrice } = usePricingContext();

  const { seasonalPrice } = priceRange;
  const isSeasonal = !!seasonalPrice;
  const priceAmount = seasonalPrice?.min ?? priceRange.amount;

  const handleAddDatesClick = () => {
    const channel =
      deviceType === 'smartphone' || deviceType === 'tablet'
        ? OPEN_BOOKNOW_DATEPICKER_MOBILE
        : OPEN_BOOKNOW_DATEPICKER_DESKTOP;
    Mediator.publish(channel);
  };

  const getSeasonalBannerContent = () => {
    if (!isSeasonal) return null;

    if (!hasDates) {
      return (
        <div className="listing-payments-module__banner-content">
          <span>{trans('listing.payments-module.seasonal.no-dates')}</span>
          <Button color="link" size="small" onClick={handleAddDatesClick}>
            {trans('listing.payments-module.add-dates')}
          </Button>
        </div>
      );
    }

    return <span>{trans('listing.payments-module.seasonal.with-dates')}</span>;
  };

  const seasonalBannerContent = getSeasonalBannerContent();

  return (
    <ListingSectionObservable section="pricing" bordered={!isListingSimplificationBottomVariant}>
      <div className="listing-payments-module">
        <ListingTitle role="secondary">
          {trans('listing.pricing.title.standalone')}
        </ListingTitle>
        <ListRow
          withBorder={false}
          renderLeftBlock={() => (
            <p>{trans('listing.pricing.monthly-rent')}</p>
          )}
          renderRightBlock={() => (
            <p className="listing-payments-module__price">
              {isSeasonal && (
                <span className="listing-payments-module__price-prefix">
                  {trans('rentable_unit_price.label.from')}
                </span>
              )}
              <span className="listing-payments-module__price-amount">
                {formatPrice(priceAmount)}
              </span>
              <span className="listing-payments-module__price-suffix">
                {trans('property_show.book_now_card.per_month')}
              </span>
            </p>
          )}
        />
        <PaymentInfoModalButton
          linkText={trans('listing.payments-module.view-all-payments')}
          linkColor="link"
          linkSize="small"
          linkClassName="listing-payments-module__cta"
          dataTest="listing-payments-module-view-all"
        />
        {seasonalBannerContent ? (
          <InfoBanner
            className="listing-payments-module__banner"
            theme="info"
            closable={false}
            icon={<DSIcon name="info" size="m" />}
            content={seasonalBannerContent}
          />
        ) : null}
      </div>
    </ListingSectionObservable>
  );
};

ListingPaymentsModule.propTypes = {
  hasDates: PropTypes.bool,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired
};

export default ListingPaymentsModule;
